
namespace Matrix.Data {
    export interface DataColumn {
        __format?: (ctx: Slick.FormatterContext) => string;
    }
}

namespace Matrix.Dashboards {

    export interface TableWidgetOptions extends IWidgetOptions {
        DatasetCode: string;
        ColumnList?: Data.DataColumn[];
        RowsPerPage?: number;
        Summaries?: boolean;
        Striped?: boolean;
        FullWidth?: boolean;
    }

    export function renderSelectDatasetMessage(element: JQuery, editor: IDashboardDesigner) {
        if (!editor)
            return false;

        element.on('click', '.select-dataset', () => {
            editor.openDesigner('DatasetCode');
        });

        element.html(`<div class="centered-widget-msg">
    Please select <a class="select-dataset" href="javascript:;">a dataset</a>...
</div>`);

        return true;
    }

    function sortIndex(sort: string[], name: string, desc: boolean) {
        if (!name || sort == null)
            return -1;
        name = name.toLowerCase();
        if (desc)
            name += " desc";
        return Q.indexOf(sort, x => x?.toLowerCase() === name);
    }

    @Serenity.Decorators.registerClass('Matrix.Dashboards.TableWidget')
    export class TableWidget extends Serenity.Widget<TableWidgetOptions> {

        private data: any[];
        private response: DatasetFetchResponse;
        private toPage: number = 0;
        private sort: string[];
        private aggregateFields: AggregateField[];
        private visibleColumns: Data.DataColumn[];

        constructor(element: JQuery, opt: TableWidgetOptions) {
            super(element, opt);

            this.element.on('click', '.page-link', e => {
                if (e.currentTarget.classList.contains('disabled'))
                    return;

                var pg = Q.toId($(e.currentTarget).parent().attr('data-lp'));
                if (pg != null) {
                    this.toPage = pg;
                    this.refresh();
                }
            });

            element.on('click', 'thead>tr>th', (e) => {
                var name = $(e.currentTarget).attr('data-name');
                if (name) {
                    var dscIndex: number;
                    var ascIndex = sortIndex(this.sort, name, false);
                    if (ascIndex >= 0) {
                        this.sort.splice(ascIndex, 1);
                    }
                    else {
                        dscIndex = sortIndex(this.sort, name, true);
                        if (dscIndex >= 0)
                            this.sort.splice(dscIndex, 1);
                    }

                    if (!e.ctrlKey) {
                        if (!e.shiftKey)
                            this.sort = [];

                        this.sort.push(name + (ascIndex >= 0 ? ' DESC' : ''));
                    }

                    this.refresh();
                }
            });

            if (!this.options.DatasetCode) {
                renderSelectDatasetMessage(this.element, this.options.__designer);
                return;
            }

            if (this.options.ColumnList) {
                var sortedCols = this.options.ColumnList.filter(x => x.SortOrder);
                if (sortedCols.length) {
                    sortedCols.sort((x, y) => Math.abs(x.SortOrder) < Math.abs(y.SortOrder) ? -1 :
                        (Math.abs(x.SortOrder) > Math.abs(y.SortOrder) ? 1 : 0));

                    this.sort = [];
                    for (var s of sortedCols)
                        this.sort.push(s.Name + (s.SortOrder < 0 ? " DESC" : ""));
                }

                if (this.options.Summaries) { 
                    this.aggregateFields = this.options.ColumnList.filter(x => x.SummaryType != null && 
                        x.SummaryType != Serenity.SummaryType.Disabled &&
                        x.SummaryType != Serenity.SummaryType.None).map(x => <AggregateField>{
                            Field: x.Name,
                            Type: x.SummaryType as any
                        });
                }
            }

            element.on('click', 'tfoot>tr>td', e => {
                e.preventDefault();

                var colIndex = $(e.currentTarget).index();
                var column = this.visibleColumns[colIndex]
                if (!column || !column.Name || column.SummaryType == Serenity.SummaryType.Disabled)
                    return;

                var aggField = Q.tryFirst(this.aggregateFields || [], x => x.Field == column.Name);
                var oldType = aggField?.Type;

                summaryTypePopup(e, oldType, newType => {
                    this.aggregateFields = this.aggregateFields || [];
                    aggField = Q.tryFirst(this.aggregateFields, x => x.Field == column.Name);
                    if (aggField)
                        aggField.Type = newType;
                    else {
                        this.aggregateFields.push({
                            Field: column.Name,
                            Type: newType
                        });
                    }
                    this.refresh();
                });
            });

            this.refresh();
        }

        protected refresh() {
            if (!this.options.DatasetCode) {
                this.data = [];
                return;
            }

            var pageSize = this.response && this.response.Take ? this.response.Take : this.options.RowsPerPage > 0 ? this.options.RowsPerPage : 2500;
            var skip = (this.toPage > 0 ? (this.toPage - 1) : 0) * pageSize;
            this.toPage = 0;
            DatasetService.Fetch({
                DatasetCode: this.options.DatasetCode,
                Params: this.options.__getDatasetParams(),
                AggregateFields: this.aggregateFields,
                Skip: skip,
                Take: pageSize,
                Sort: this.sort
            },
                (response) => {
                    var data = response.Data || [];
                    if (!Q.isArray(data))
                        data = [data];
                    this.data = data;
                    this.response = response;
                    this.updateContent();
                });
        }

        static extractColumnsFromData(data: any[]): Data.DataColumn[] {
            var columnUsed: Q.Dictionary<boolean> = {};
            var columns: Data.DataColumn[] = [];

            var index = 0;
            for (var item of data) {
                if (item == null)
                    continue;

                if (index++ > 10)
                    break;

                for (var k of Object.keys(item)) {
                    if (columnUsed[k])
                        continue;

                    columns.push({
                        Name: k,
                        Title: Matrix.Data.titleize(k),
                        Alignment: typeof item[k] === "number" ? "right" : undefined
                    });

                    columnUsed[k] = true;
                }
            }

            return columns;
        }

        protected updateContent(): void {
            this.element.html('');

            var columns = this.visibleColumns = (this.options.ColumnList ?? TableWidget.extractColumnsFromData(this.data))
                .filter(x => x.Visible == null || x.Visible);

            if (!columns.length)
                return;

            var sb = ['<div class="table-scroller">'];

            sb.push('<table class="table table-sm');
            if (!this.options.FullWidth)
                sb.push(' w-auto');

            if (this.options.Striped)
                sb.push(' table-striped');

            sb.push(' dashboard-widget-table"><thead><tr>');

            for (var col of columns) {

                sb.push('<th');

                sb.push(' data-name="');
                sb.push(Q.attrEncode(col.Name));
                sb.push('"');

                if (col.Alignment != null) {
                    sb.push(' class="align-');
                    sb.push(Q.attrEncode(col.Alignment));
                    sb.push('"');
                }
                if (col.Width) {
                    sb.push(' style="width: ');
                    sb.push(Q.attrEncode(col.Width + 'px'));
                    sb.push('"');
                }
                sb.push('>');

                sb.push(Q.htmlEncode(col.Title ?? col.Name));

                if (sortIndex(this.sort, col.Name, false) >= 0)
                    sb.push('<i class="sort-indicator sort-indicator-asc fa fa-caret-up"></i>');
                else if (sortIndex(this.sort, col.Name, true) >= 0)
                    sb.push('<i class="sort-indicator sort-indicator-desc fa fa-caret-down"></i>');

                sb.push('</th>');

                if (col.FormatterType != null && col.FormatterType.length) {
                    try {
                        var formatterType = Serenity.FormatterTypeRegistry.get(col.FormatterType) as any;
                        var formatter = new formatterType() as Slick.Formatter;
                        var formatterParams = Q.extend({}, col.FormatterParams) as any;
                        if (!formatterParams.displayFormat && col.DisplayFormat) {
                            formatterParams.displayFormat = col.DisplayFormat;
                        }
                        Serenity.ReflectionOptionsSetter.set(formatter, formatterParams);
                        col.__format = ((fmt) => {
                            return ctx => fmt.format(ctx);
                        })(formatter);
                    }
                    catch {
                        delete col.__format;
                    }
                }
            }

            sb.push('</tr></thead><tbody>')

            var ctx: Slick.FormatterContext = {
                row: 0,
                column: {
                } as Slick.Column
            };

            function renderTD(col: Data.DataColumn, skipContent?: boolean) {
                sb.push('<td')

                var klasses = [];

                if (col.Alignment) {
                    klasses.push(' align-' + col.Alignment);
                }

                ctx.column.field = col.Name;

                var fmtClass = ctx.item[col.Name + '_format_'];
                if (fmtClass != null) {
                    klasses.push('fmt-' + fmtClass);
                }

                if (klasses.length) {
                    sb.push(' class= "');
                    sb.push(klasses.map(x => Q.attrEncode(x)).join(' '));
                    sb.push('"');
                }

                if (col.Width) {
                    sb.push(' style="width: ');
                    sb.push(Q.attrEncode(col.Width + 'px'));
                    sb.push('"');
                }
                sb.push('>');

                if (!skipContent) {
                    var content = undefined;

                    if (col.__format) {
                        try {
                            content = col.__format(ctx);
                        }
                        catch {
                        }
                    }

                    if (content === undefined && col.DisplayFormat) {
                        if (typeof ctx.value == "number")
                            content = Q.htmlEncode(Q.formatNumber(ctx.value, col.DisplayFormat));
                        else if (typeof ctx.value === "string" && Q.parseISODateTime(ctx.value) ||
                            ctx.constructor === Date)
                            content = Q.htmlEncode(Q.formatDate(ctx.value, col.DisplayFormat));
                    }

                    if (content === undefined)
                        content = Q.htmlEncode(ctx.value);

                    sb.push(content);
                }

                sb.push('</td>');
            }

            for (var item of this.data) {
                ctx.item = item;
                ctx.row++;
                ctx.cell = 0;

                sb.push('<tr');

                if (item._row_format_ != null) {
                    sb.push(' class="fmt-');
                    sb.push(Q.attrEncode(item._row_format_));
                    sb.push('"');
                }

                sb.push('>');

                for (var col of columns) {
                    ctx.cell++;
                    ctx.value = item[col.Name];

                    renderTD(col);
                }

                sb.push('</tr>');
            }

            sb.push('</tbody>');

            if (this.options.Summaries) {
                sb.push('<tfoot><tr>');

                ctx.item = {};
                ctx.row = -1;
                ctx.cell = 0;
                var respAgg = this.response?.Aggregates;
                var aggregates = (respAgg || [])[0]?.Values ?? {};

                for (var col of columns) {
                    ctx.cell++;
                    ctx.value = null;
                    var aggField = Q.tryFirst(this.aggregateFields || [], x => x.Field == col.Name);
                    if (!aggField) {
                        renderTD(col, true);
                        continue;
                    }

                    var aggs = aggregates[col.Name] ?? {};
                    var agg = aggs[AggregateType[aggField.Type]] ?? aggs[aggField.Type];
                    if (agg == null) {
                        renderTD(col, true);
                        continue;
                    }

                    ctx.value = agg;
                    ctx.item = { [col.Name]: agg };                   
                    renderTD(col);
                }

                sb.push('</tr></tfoot>');
            }

            sb.push('</table></div>');

            if (this.data.length &&
                this.response &&
                this.response.Take && 
                (this.response.Skip ||
                 this.response.More ||
                 this.response.TotalCount > this.data.length)) {

                var pageSize = this.response.Take || this.data.length || 2500;
                var page = Math.max(Math.floor(this.response.Skip / pageSize) + 1, 1);
                var total = this.response.TotalCount ? (Math.max(page, Math.floor(this.response.TotalCount / pageSize) + 1)) :
                    (this.response.More ? page + 1 : page);

                sb.push(bsPagerMarkup({
                    page: page,
                    total: total,
                    first: null,
                    last: null,
                    maxVisible: 1
                }));
            }

            this.element.html(sb.join(''));
        }
    }

    var summaryTypePopupEl: JQuery;

    export function summaryTypePopup(e: JQueryEventObject, oldType: AggregateType, select: (newType: AggregateType) => void) {

        if (!summaryTypePopupEl) {
            summaryTypePopupEl = $(`<ul class="s-AggregatesPopup"
                style="display:none;position:absolute;z-index:100000;">
                <li data-agg="1">Sum</li>
                <li data-agg="2">Average</li>
                <li data-agg="3">Min</li>
                <li data-agg="4">Max</li>
            </ul>`).appendTo(document.body);
        }

        summaryTypePopupEl.off('click').click(e => {
            if (!$(e.target).is("li"))
                return;

            var newType = Q.toId($(e.target).data('agg'));
            if (newType == null || oldType == newType)
                return;
            select(newType);
        });

        summaryTypePopupEl.show()
            .css('top', (e.pageY - summaryTypePopupEl.height()) + 'px')
            .css('left', e.pageX + 'px')
            .children('li')
            .removeClass('active')
            .end()
            .children('li[data-agg=' + oldType + ']')
            .addClass('active');

        $(document.body).off('click.summaryTypePopup');

        window.setTimeout(() => $(document.body)
            .on("click.summaryTypePopup", () => {
                summaryTypePopupEl?.hide();
                $(document.body).off('click.summaryTypePopup');
            }), 1);
    }
}
